/**
 * @fileoverview Content With Annotations Component
 *
 * Renders markdown content with inline highlighted annotations from AI extracts.
 * Desktop: Two-column layout with content on left and comments panel on right.
 * Mobile: Single column with inline comments below each highlight.
 */

import { useState, useRef, useLayoutEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

/**
 * Sentiment colors for highlights and comments
 */
const SENTIMENT_COLORS = {
  positive: {
    highlight: 'bg-green-500/20 hover:bg-green-500/30 dark:bg-green-500/30 dark:hover:bg-green-500/40',
    highlightActive: 'bg-green-500/40 dark:bg-green-500/50',
    border: 'border-green-500',
    badge: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200',
  },
  neutral: {
    highlight: 'bg-gray-400/20 hover:bg-gray-400/30 dark:bg-gray-400/30 dark:hover:bg-gray-400/40',
    highlightActive: 'bg-gray-400/40 dark:bg-gray-400/50',
    border: 'border-gray-400 dark:border-gray-500',
    badge: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  },
  negative: {
    highlight: 'bg-red-500/20 hover:bg-red-500/30 dark:bg-red-500/30 dark:hover:bg-red-500/40',
    highlightActive: 'bg-red-500/40 dark:bg-red-500/50',
    border: 'border-red-500',
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
  },
};

/**
 * Check if content has embedded extract markers
 * @param {string} content - The markdown content
 * @returns {boolean} True if content has <!--extract:N--> markers
 */
function hasExtractMarkers(content) {
  if (!content) return false;
  return /<!--extract:\d+-->/.test(content);
}

/**
 * Get which extract indices have markers in the content
 * @param {string} content - The markdown content
 * @returns {Set<number>} Set of extract indices that have markers
 */
function getMarkerIndices(content) {
  const indices = new Set();
  if (!content) return indices;

  const markerRegex = /<!--extract:(\d+)-->/g;
  let match;
  while ((match = markerRegex.exec(content)) !== null) {
    indices.add(parseInt(match[1], 10));
  }
  return indices;
}

/**
 * Replace extract markers with <mark> tags for highlighting
 * @param {string} content - Markdown content with <!--extract:N--> markers
 * @param {Array} extracts - Extract objects (for sentiment lookup)
 * @returns {string} Content with markers replaced by <mark> tags
 */
function replaceMarkersWithHighlights(content, extracts) {
  if (!content || !extracts || extracts.length === 0) {
    return content;
  }

  // Replace each marker pair with <mark> tags
  return content.replace(
    /<!--extract:(\d+)-->([\s\S]*?)<!--\/extract:\1-->/g,
    (match, indexStr, text) => {
      const index = parseInt(indexStr, 10);
      const extract = extracts[index];
      const sentiment = extract?.sentiment || 'neutral';
      return `<mark data-extract-index="${index}" data-sentiment="${sentiment}">${text}</mark>`;
    }
  );
}

/**
 * Strip extract markers from content (for edit mode)
 * @param {string} content - Content that may have markers
 * @returns {string} Content with markers removed
 */
function stripExtractMarkers(content) {
  if (!content) return content;
  return content.replace(/<!--\/?extract:\d+-->/g, '');
}

/**
 * Comment card component for displaying extract analysis
 */
function CommentCard({ extract, isActive, onMouseEnter, onMouseLeave, onClick, style }) {
  const colors = SENTIMENT_COLORS[extract.sentiment] || SENTIMENT_COLORS.neutral;

  return (
    <div
      className={`
        absolute left-0 right-0 rounded-lg border-l-4 p-3 transition-all duration-200 cursor-pointer
        ${colors.border}
        ${isActive ? 'bg-white dark:bg-gray-800 shadow-lg ring-2 ring-purple-300 dark:ring-purple-500 z-10' : 'bg-gray-50 dark:bg-gray-900 hover:bg-white dark:hover:bg-gray-800 hover:shadow'}
      `}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-2">
        <span className={`text-xs px-2 py-0.5 rounded ${colors.badge}`}>
          {extract.sentiment}
        </span>
        {extract.scoreImpact != null && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Impact: {extract.scoreImpact > 0 ? '+' : ''}{extract.scoreImpact?.toFixed(1) || '0.0'}
          </span>
        )}
      </div>
      <blockquote className="text-xs text-gray-600 dark:text-gray-300 italic border-l-2 border-gray-300 dark:border-gray-600 pl-2 mb-2 line-clamp-2">
        &ldquo;{extract.text}&rdquo;
      </blockquote>
      <p className="text-xs text-gray-700 dark:text-gray-200">{extract.analysis}</p>
    </div>
  );
}

/**
 * Mobile inline comment that expands on tap
 */
function MobileInlineComment({ extract, isExpanded, onToggle }) {
  const colors = SENTIMENT_COLORS[extract.sentiment] || SENTIMENT_COLORS.neutral;

  return (
    <div
      className={`
        my-2 rounded-lg border-l-4 overflow-hidden transition-all duration-200
        ${colors.border} bg-gray-50 dark:bg-gray-900
      `}
    >
      <button
        onClick={onToggle}
        className="w-full px-3 py-2 text-left flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded ${colors.badge}`}>
            {extract.sentiment}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[150px]">
            {extract.text.substring(0, 30)}...
          </span>
        </div>
        <svg
          className={`h-4 w-4 text-gray-400 dark:text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isExpanded && (
        <div className="px-3 pb-3 space-y-2">
          <blockquote className="text-xs text-gray-600 dark:text-gray-300 italic border-l-2 border-gray-300 dark:border-gray-600 pl-2">
            &ldquo;{extract.text}&rdquo;
          </blockquote>
          <p className="text-xs text-gray-700 dark:text-gray-200">{extract.analysis}</p>
          {extract.scoreImpact != null && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Score impact: {extract.scoreImpact > 0 ? '+' : ''}{extract.scoreImpact?.toFixed(1)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Highlight mark component
 */
function HighlightMark({ children, activeIndex, onSetActive, onToggleMobile, ...props }) {
  const indexStr = props['data-extract-index'];
  const sentiment = props['data-sentiment'] || 'neutral';
  const index = parseInt(indexStr, 10);
  const colors = SENTIMENT_COLORS[sentiment] || SENTIMENT_COLORS.neutral;
  const isActive = activeIndex === index;

  return (
    <mark
      className={`
        cursor-pointer rounded px-0.5 transition-colors duration-200
        ${isActive ? colors.highlightActive : colors.highlight}
      `}
      onMouseEnter={() => onSetActive(index)}
      onMouseLeave={() => onSetActive(null)}
      onClick={() => {
        onSetActive(index);
        onToggleMobile(index);
      }}
      data-extract-index={indexStr}
      data-sentiment={sentiment}
    >
      {children}
    </mark>
  );
}

/**
 * Main ContentWithAnnotations component
 */
export default function ContentWithAnnotations({ content, extracts, isEditing = false }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const [expandedMobile, setExpandedMobile] = useState({});
  const [commentPositions, setCommentPositions] = useState({});
  const desktopContentRef = useRef(null);
  const mobileContentRef = useRef(null);
  const commentsRef = useRef(null);
  const positionsCalculated = useRef(false);

  // Check if content uses embedded markers (new system) vs needing offset-based highlighting (legacy)
  const contentHasMarkers = useMemo(() => hasExtractMarkers(content), [content]);

  // Get which extract indices have markers in the content
  const markerIndices = useMemo(() => getMarkerIndices(content), [content]);

  // Process extracts - add originalIndex for reference
  const processedExtracts = useMemo(() => {
    if (!extracts || extracts.length === 0) return [];
    return extracts.map((extract, idx) => ({ ...extract, originalIndex: idx }));
  }, [extracts]);

  // Determine which extracts are highlightable (have markers in content)
  const highlightableExtracts = useMemo(() => {
    if (!contentHasMarkers) return [];
    return processedExtracts.filter((e) => markerIndices.has(e.originalIndex));
  }, [processedExtracts, contentHasMarkers, markerIndices]);

  // Extracts without markers (will be shown in "Additional Notable Sections")
  const nonHighlightableExtracts = useMemo(() => {
    if (!contentHasMarkers) {
      // No markers in content - all extracts are non-highlightable
      return processedExtracts;
    }
    return processedExtracts.filter((e) => !markerIndices.has(e.originalIndex));
  }, [processedExtracts, contentHasMarkers, markerIndices]);

  // Prepare content for display
  const highlightedContent = useMemo(() => {
    if (isEditing) {
      // Strip markers when editing so they don't show in textarea
      return stripExtractMarkers(content);
    }

    if (!contentHasMarkers || highlightableExtracts.length === 0) {
      // No markers or no extracts - return content as-is (with markers stripped for safety)
      return stripExtractMarkers(content) || content;
    }

    // Replace markers with <mark> tags for highlighting
    return replaceMarkersWithHighlights(content, extracts);
  }, [content, extracts, contentHasMarkers, highlightableExtracts.length, isEditing]);

  // Calculate comment positions after DOM renders
  useLayoutEffect(() => {
    if (isEditing || !desktopContentRef.current || highlightableExtracts.length === 0) {
      return;
    }

    // Delay to ensure markdown and images have rendered
    const timer = setTimeout(() => {
      const contentEl = desktopContentRef.current;
      if (!contentEl) return;

      const contentRect = contentEl.getBoundingClientRect();
      const newPositions = {};
      const COMMENT_HEIGHT = 140;
      const COMMENT_GAP = 8;

      // Get all highlight positions
      const highlightPositions = [];
      highlightableExtracts.forEach((extract) => {
        const markEl = contentEl.querySelector(`mark[data-extract-index="${extract.originalIndex}"]`);
        if (markEl) {
          const markRect = markEl.getBoundingClientRect();
          highlightPositions.push({
            index: extract.originalIndex,
            top: markRect.top - contentRect.top,
          });
        }
      });

      // Sort by vertical position
      highlightPositions.sort((a, b) => a.top - b.top);

      // Position comments, preventing overlap
      let lastBottom = 0;
      highlightPositions.forEach(({ index, top }) => {
        // Try to position at highlight level, but don't overlap with previous
        const adjustedTop = Math.max(top, lastBottom);
        newPositions[index] = adjustedTop;
        lastBottom = adjustedTop + COMMENT_HEIGHT + COMMENT_GAP;
      });

      setCommentPositions(newPositions);
      positionsCalculated.current = true;
    }, 100);

    return () => clearTimeout(timer);
  }, [highlightedContent, highlightableExtracts, isEditing]);

  // Handle mobile toggle
  const handleToggleMobile = (index) => {
    setExpandedMobile((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Scroll to highlight when clicking a comment card
  const scrollToHighlight = (index) => {
    if (!desktopContentRef.current) return;
    const highlightEl = desktopContentRef.current.querySelector(`mark[data-extract-index="${index}"]`);
    if (highlightEl) {
      highlightEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setActiveIndex(index);
    }
  };

  // Create stable component for ReactMarkdown
  const markdownComponents = useMemo(() => ({
    mark: (props) => (
      <HighlightMark
        {...props}
        activeIndex={activeIndex}
        onSetActive={setActiveIndex}
        onToggleMobile={handleToggleMobile}
      />
    ),
  }), [activeIndex]);

  // If no extracts or editing, render simple markdown
  if (isEditing || !extracts || extracts.length === 0) {
    return (
      <div className="prose prose-sm dark:prose-invert max-w-none">
        <ReactMarkdown>{content}</ReactMarkdown>
      </div>
    );
  }

  // Calculate total height needed for comments panel
  const sortedPositions = Object.entries(commentPositions)
    .map(([idx, top]) => ({ index: parseInt(idx), top }))
    .sort((a, b) => a.top - b.top);
  const lastPosition = sortedPositions[sortedPositions.length - 1];
  const commentsHeight = lastPosition ? lastPosition.top + 170 : 'auto';

  return (
    <div className="relative">
      {/* Desktop layout: Grid with content and comments side panel */}
      <div className="hidden lg:grid lg:grid-cols-[1fr_280px] lg:gap-6">
        {/* Content column */}
        <div ref={desktopContentRef} className="prose prose-sm dark:prose-invert max-w-none">
          <ReactMarkdown
            rehypePlugins={[rehypeRaw]}
            components={markdownComponents}
          >
            {highlightedContent}
          </ReactMarkdown>
        </div>

        {/* Comments panel - absolutely positioned comments */}
        <div
          ref={commentsRef}
          className="relative pb-4"
          style={{ minHeight: commentsHeight }}
        >
          {highlightableExtracts.map((extract) => {
            const isActive = activeIndex === extract.originalIndex;
            const top = commentPositions[extract.originalIndex];

            // Don't render until we have positions
            if (top === undefined) return null;

            return (
              <CommentCard
                key={extract.originalIndex}
                extract={extract}
                isActive={isActive}
                onMouseEnter={() => setActiveIndex(extract.originalIndex)}
                onMouseLeave={() => setActiveIndex(null)}
                onClick={() => scrollToHighlight(extract.originalIndex)}
                style={{ top: `${top}px` }}
              />
            );
          })}
        </div>
      </div>

      {/* Mobile layout: Single column with inline comments */}
      <div className="lg:hidden">
        <div ref={mobileContentRef} className="prose prose-sm dark:prose-invert max-w-none">
          <ReactMarkdown
            rehypePlugins={[rehypeRaw]}
            components={markdownComponents}
          >
            {highlightedContent}
          </ReactMarkdown>
        </div>

        {/* Mobile comments section below content */}
        {highlightableExtracts.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-xs font-medium text-purple-700 dark:text-purple-300 mb-3">Notable Sections</h4>
            <div className="space-y-2">
              {highlightableExtracts.map((extract) => (
                <MobileInlineComment
                  key={extract.originalIndex}
                  extract={extract}
                  isExpanded={expandedMobile[extract.originalIndex] || activeIndex === extract.originalIndex}
                  onToggle={() => handleToggleMobile(extract.originalIndex)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Show non-highlightable extracts (no markers in content) at bottom */}
      {nonHighlightableExtracts.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">
            Additional Notable Sections ({nonHighlightableExtracts.length} without content markers)
          </h4>
          <div className="space-y-2">
            {nonHighlightableExtracts.map((extract) => (
              <MobileInlineComment
                key={extract.originalIndex}
                extract={extract}
                isExpanded={expandedMobile[extract.originalIndex]}
                onToggle={() => handleToggleMobile(extract.originalIndex)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
